/// Клавіатура каси. SPEC: Zala Handoff Spec §2.
///
/// Ціль — продаж абонемента за 40 секунд без миші. Тому це домен, а не деталь
/// компонента: розкладка клавіш — продуктове рішення, яке треба читати в одному
/// місці і тестувати без браузера.
///
/// Два правила, які легко зламати:
/// 1. F-ряд перехоплюється ЗАВЖДИ (`preventDefault`) — інакше F5 перезавантажує
///    сторінку з непорожнім кошиком.
/// 2. Цифри 1–8 працюють лише коли фокус НЕ в полі вводу — інакше касир не
///    може набрати «2» у сумі.

export type PosAction =
  | 'goto_checkin'
  | 'focus_search'
  | 'new_client'
  | 'price_list'
  | 'discount'
  | 'pay_cash'
  | 'pay_card'
  | 'pay_deposit'
  | 'pay_debt'
  | 'checkout'
  | 'reprint_last'
  | 'help'
  | 'quick_slot'
  | 'submit'
  | 'cancel'
  | 'move_down'
  | 'move_up'
  | 'move_first'
  | 'move_last'

export type Hotkey = {
  key: string
  action: PosAction
  label: string
  /// Умови й обмеження — те, що показує довідка на F12.
  note: string
  /// Чи робить клавіша хоч щось СЬОГОДНІ. `false` — підписника немає або
  /// обробник порожній (D98).
  ///
  /// Поле, а не здогад по тексту нотатки: довідка мусить відрізняти «працює з
  /// умовою» від «не працює взагалі», і робити це за рядком «Поки не працює»
  /// означало б зламати довідку на першому ж переписуванні копії.
  works?: false
}

export const HOTKEYS: readonly Hotkey[] = [
  // F1 лишається в переліку заради `preventDefault` — інакше вона відкриє
  // довідку браузера просто на касі. Але дії за нею немає: підписника
  // `goto_checkin` не існує в жодному екрані, а голий перехід спалив би
  // набраний кошик (він у стані компонента). Нотатка каже те, що є, а не те,
  // що планувалось: обіцянка «попереджає, якщо кошик не порожній» описувала
  // механізм, якого в коді немає (Q84).
  {
    key: 'F1',
    action: 'goto_checkin',
    label: 'Чекін',
    note: 'Поки не працює — перехід зітер би кошик.',
    works: false,
  },
  { key: 'F2', action: 'focus_search', label: 'Пошук клієнта', note: 'Фокус + виділення тексту.' },
  // D1: одне значення на всіх екранах. Прайсу на чекіні немає, а «додати
  // людину» потрібно і там, і в списку, і на касі — тому F3 закріплена за
  // клієнтом, а каталог поїхав у цифровий ряд.
  { key: 'F3', action: 'new_client', label: 'Новий клієнт', note: 'Однаково на всіх екранах.' },
  { key: 'F4', action: 'discount', label: 'Промокод / знижка', note: 'Понад 15% — право менеджера.' },
  { key: 'F5', action: 'pay_cash', label: 'Готівка', note: 'Покриває весь залишок; повторно — скидає.' },
  { key: 'F6', action: 'pay_card', label: 'Картка', note: 'Покриває весь залишок; повторно — скидає.' },
  { key: 'F7', action: 'pay_deposit', label: 'Депозит', note: 'Обмежено доступним балансом.' },
  { key: 'F8', action: 'pay_debt', label: 'У борг', note: 'Тільки менеджер; пише в журнал.' },
  { key: 'F9', action: 'checkout', label: 'Оплатити і фіскалізувати', note: 'Активна лише при залишку 0.' },
  // Нотатка каже те саме, що кнопка «Копія чека» в квитанції: друкувати нема на
  // чому (`printerReady === false`, транспорту `reprint` ще немає). Кнопка про
  // це говорить, а довідка обіцяла клавішу без застережень — і рецепція, яка
  // читає довідку, тисне F10 при живій людині (D98).
  {
    key: 'F10',
    action: 'reprint_last',
    label: 'Копія останнього чека',
    note: 'Поки не працює — принтера робочого місця ще немає.',
    works: false,
  },
  { key: 'F12', action: 'help', label: 'Довідка по клавішах', note: 'Esc закриває.' },
  { key: '1–8', action: 'quick_slot', label: 'Позиція з «Популярного»', note: 'Ігнорується у полі вводу.' },
  { key: '0', action: 'price_list', label: 'Весь прайс', note: '«Усе решта» після восьми популярних.' },
  { key: '↑ ↓', action: 'move_down', label: 'Активний рядок у пошуку', note: 'Без циклу; Enter бере активний.' },
  { key: 'Enter', action: 'submit', label: 'Перший результат → інакше оплата', note: 'Після чека — новий продаж.' },
  { key: 'Esc', action: 'cancel', label: 'Каскадне скасування', note: 'Модалка → пошук → розбивка → кошик.' },
]

const F_ROW: Record<string, PosAction> = Object.fromEntries(
  HOTKEYS.filter((hotkey) => /^F\d+$/.test(hotkey.key)).map((hotkey) => [hotkey.key, hotkey.action]),
)

export type KeyEventLike = {
  key: string
  target?: EventTarget | null
  ctrlKey?: boolean
  metaKey?: boolean
  altKey?: boolean
}

/// Чи стоїть курсор у полі, де цифри означають цифри.
export const isTyping = (target: EventTarget | null | undefined): boolean => {
  if (!target || typeof target !== 'object' || !('tagName' in target)) return false
  const element = target as HTMLElement
  if (element.isContentEditable) return true
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName)
}

export type Resolved =
  | { action: PosAction; slot?: number; preventDefault: boolean }
  | null

/// Що робити з натисканням. Чиста функція — тестується без DOM.
export const resolveKey = (event: KeyEventLike): Resolved => {
  // Модифікатори віддаємо браузеру: Ctrl+F, Cmd+R і решта мусять працювати як
  // усюди, інакше каса стає в'язницею.
  if (event.ctrlKey || event.metaKey || event.altKey) return null

  const fAction = F_ROW[event.key]
  if (fAction) return { action: fAction, preventDefault: true }

  if (event.key === 'F12') return { action: 'help', preventDefault: true }
  if (event.key === 'Escape') return { action: 'cancel', preventDefault: false }
  if (event.key === 'Enter') {
    return isTyping(event.target) ? null : { action: 'submit', preventDefault: false }
  }

  // D5. Стрілки працюють І в полі вводу: касир набирає прізвище і одразу
  // веде активний рядок униз, не знімаючи фокуса. preventDefault обовʼязковий —
  // інакше сторінка скролиться під ним.
  if (event.key === 'ArrowDown') return { action: 'move_down', preventDefault: true }
  if (event.key === 'ArrowUp') return { action: 'move_up', preventDefault: true }
  if (event.key === 'Home' && !isTyping(event.target)) {
    return { action: 'move_first', preventDefault: true }
  }
  if (event.key === 'End' && !isTyping(event.target)) {
    return { action: 'move_last', preventDefault: true }
  }

  if (/^[1-8]$/.test(event.key) && !isTyping(event.target)) {
    return { action: 'quick_slot', slot: Number(event.key), preventDefault: true }
  }

  // D1. Нуль — «усе решта» після восьми популярних позицій.
  if (event.key === '0' && !isTyping(event.target)) {
    return { action: 'price_list', preventDefault: true }
  }

  return null
}

// ── сканер ──────────────────────────────────────────────────────────────────

/// Сканер штрихкодів і RFID — це «клавіатура, що друкує швидко»: серія
/// символів із інтервалом менше 30 мс і Enter у кінці.
///
/// Розпізнається глобально, навіть без активного поля — касир не має спершу
/// клікати в інпут. І саме тому цифри зі скану НЕ повинні потрапляти в
/// обробник «1–8»: різниця тільки в інтервалі, більше нічого немає.
export const SCAN_MAX_GAP_MS = 30
export const SCAN_MIN_LENGTH = 4

export type ScanState = { buffer: string; lastAt: number }

export const emptyScan = (): ScanState => ({ buffer: '', lastAt: 0 })

export type ScanResult =
  | { kind: 'scanning'; state: ScanState }
  | { kind: 'code'; code: string; state: ScanState }
  | { kind: 'not_a_scan'; state: ScanState }

export const feedScanner = (state: ScanState, key: string, at: number): ScanResult => {
  const fast = at - state.lastAt <= SCAN_MAX_GAP_MS

  if (key === 'Enter') {
    // Enter завершує скан лише якщо перед ним була швидка серія достатньої
    // довжини. Інакше це звичайний Enter, і його чекає форма.
    if (state.buffer.length >= SCAN_MIN_LENGTH && fast) {
      return { kind: 'code', code: state.buffer, state: emptyScan() }
    }
    return { kind: 'not_a_scan', state: emptyScan() }
  }

  if (key.length !== 1) return { kind: 'not_a_scan', state: emptyScan() }

  const buffer = fast || state.buffer === '' ? state.buffer + key : key
  return { kind: 'scanning', state: { buffer, lastAt: at } }
}
